import { useMemo } from "react"
import {
  Business,
  Client,
  ConditionGroup,
  Contact,
  ContactListResponse,
  HouseholdPerson,
  NextPageParams,
  PaginationMetadata,
  Prospect,
  Report,
  ReportWithCohorts,
  ViewSort,
} from "@/schema"
import { PastClient } from "@/schema/past-client"
import { useDataTableStore } from "@/stores/data-table-store"
import { normalizeReportConditionGroup, useReportConditionStore } from "@/stores/report-condition-store"
import { keepPreviousData, Query, useInfiniteQuery, useQuery } from "@tanstack/react-query"

import { BusinessesResponse, fetchBusinesses } from "@/hooks/api/businesses/api"
import { ClientsResponse, fetchAllClientIds, fetchClients } from "@/hooks/api/clients/api"
import { fetchAllContactIds, fetchContacts } from "@/hooks/api/contacts/api"
import { fetchHouseholdPeople, HouseholdPeopleResponse, HouseholdRecordType } from "@/hooks/api/household-people/api"
import { fetchAllPastClientIds, fetchPastClients, PastClientsResponse } from "@/hooks/api/past-clients/api"
import { fetchAllProspectIds, fetchProspects, ProspectsResponse } from "@/hooks/api/prospects/api"
import { fetchReport, fetchReports, ReportsResponse } from "@/hooks/api/reports/api"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

type SelectableFilterReportRecordType = "client" | "prospect" | "past_client" | "contact"

const reportRecordQueryRoots: Record<SelectableFilterReportRecordType, string> = {
  client: "clients",
  prospect: "prospects",
  past_client: "past_clients",
  contact: "contacts",
}

export function useFetchAllReportRecordIds(recordType: SelectableFilterReportRecordType, enabled = true) {
  const getContext = useApiContext()
  const searchTerm = useDataTableStore((state) => state.searchTerm)
  const conditionGroup = useReportConditionStore((state) => state.conditionGroup)
  const conditionsInitialized = useReportConditionStore((state) => state.initialized)
  const normalizedGroup = useMemo(() => normalizeReportConditionGroup(conditionGroup), [conditionGroup])

  return useQuery<{ ids: string[]; totalCount: number }>({
    queryKey: [reportRecordQueryRoots[recordType], "report", "all-ids", searchTerm, normalizedGroup],
    enabled: enabled && conditionsInitialized,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")

      switch (recordType) {
        case "client": {
          const response = await fetchAllClientIds(context, normalizedGroup, searchTerm)
          return { ids: response.client_ids, totalCount: response.total_count }
        }
        case "prospect": {
          const response = await fetchAllProspectIds(context, normalizedGroup, searchTerm)
          return { ids: response.prospect_ids, totalCount: response.total_count }
        }
        case "past_client": {
          const response = await fetchAllPastClientIds(context, normalizedGroup, searchTerm)
          return { ids: response.past_client_ids, totalCount: response.total_count }
        }
        case "contact": {
          const response = await fetchAllContactIds(context, normalizedGroup, searchTerm)
          return { ids: response.contact_ids, totalCount: response.total_count }
        }
      }
    },
  })
}

export function useFetchReports(params?: {
  conditionGroup?: ConditionGroup
  enabled?: boolean
  searchTerm?: string
  viewSort?: ViewSort
  includeCompany?: boolean
}) {
  const getContext = useApiContext()
  const bookId = useBookId()
  const { conditionGroup, enabled, searchTerm, viewSort, includeCompany } = params ?? {}

  return useQuery({
    queryKey: ["reports", bookId, conditionGroup, searchTerm, viewSort, includeCompany],
    enabled: enabled !== false,
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchReports(context, { conditionGroup, searchTerm, viewSort, includeCompany })
    },
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const hasActive = query.state.data?.reports.some(
        (report) => report.run_status === "pending" || report.run_status === "running"
      )
      return hasActive ? 3000 : false
    },
  })
}

export function useInfiniteReports(enabled?: boolean) {
  const getContext = useApiContext()
  const bookId = useBookId()
  const { searchTerm, completedConditions, viewSort } = useDataTableStore()
  const conditionGroup = useMemo<ConditionGroup>(
    () => ({
      combinator: "and",
      conditions: completedConditions,
    }),
    [completedConditions]
  )

  return useInfiniteQuery<
    ReportsResponse,
    Error,
    { reports: Report[]; pagination: PaginationMetadata },
    (string | ConditionGroup | ViewSort | undefined)[],
    NextPageParams
  >({
    queryKey: ["reports", "infinite", bookId, searchTerm, conditionGroup, viewSort],
    staleTime: 0,
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchReports(context, { conditionGroup, searchTerm, viewSort, nextPageParams: pageParam })
    },
    initialPageParam: { page: 1, per_page: 25 },
    enabled,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: (data) => ({
      reports: data.pages.flatMap((page) => page.reports),
      pagination: data.pages[data.pages.length - 1]?.pagination,
    }),
    refetchInterval: (query) => {
      const hasActive = query.state.data?.pages.some((page) =>
        page.reports.some((report) => report.run_status === "pending" || report.run_status === "running")
      )
      return hasActive ? 3000 : false
    },
  })
}

export function useFetchReport(
  id: string,
  options?: { refetchInterval?: number | false | ((query: Query<ReportWithCohorts>) => number | false) }
) {
  const getContext = useApiContext()
  return useQuery({
    queryKey: ["report", id],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchReport(context, id)
    },
    refetchInterval: options?.refetchInterval,
  })
}

export function useInfiniteReportClients(enabled?: boolean) {
  const getContext = useApiContext()
  const { searchTerm, viewSort } = useDataTableStore()
  const conditionGroup = useReportConditionStore((state) => state.conditionGroup)
  const conditionsInitialized = useReportConditionStore((state) => state.initialized)
  const normalizedGroup = useMemo(() => normalizeReportConditionGroup(conditionGroup), [conditionGroup])

  return useInfiniteQuery<
    ClientsResponse,
    Error,
    { clients: Client[]; pagination: PaginationMetadata; total_aum: number },
    unknown[],
    NextPageParams | undefined
  >({
    queryKey: ["clients", "report", "infinite", searchTerm, normalizedGroup, viewSort],
    staleTime: 0,
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchClients(context, normalizedGroup, pageParam, searchTerm, viewSort)
    },
    initialPageParam: undefined,
    enabled: enabled && conditionsInitialized,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: (data) => ({
      clients: data.pages.flatMap((page) => page.clients),
      pagination: data.pages[data.pages.length - 1]?.pagination,
      total_aum: data.pages[data.pages.length - 1]?.total_aum,
    }),
  })
}

export function useInfiniteReportProspects(enabled?: boolean) {
  const getContext = useApiContext()
  const { searchTerm, viewSort } = useDataTableStore()
  const conditionGroup = useReportConditionStore((state) => state.conditionGroup)
  const conditionsInitialized = useReportConditionStore((state) => state.initialized)
  const normalizedGroup = useMemo(() => normalizeReportConditionGroup(conditionGroup), [conditionGroup])

  return useInfiniteQuery<
    ProspectsResponse,
    Error,
    { prospects: Prospect[]; pagination: PaginationMetadata },
    unknown[],
    NextPageParams | undefined
  >({
    queryKey: ["prospects", "report", "infinite", searchTerm, normalizedGroup, viewSort],
    staleTime: 0,
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchProspects(context, normalizedGroup, pageParam, searchTerm, viewSort)
    },
    initialPageParam: undefined,
    enabled: enabled && conditionsInitialized,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: (data) => ({
      prospects: data.pages.flatMap((page) => page.prospects),
      pagination: data.pages[data.pages.length - 1]?.pagination,
    }),
  })
}

export function useInfiniteReportPastClients(enabled?: boolean) {
  const getContext = useApiContext()
  const { searchTerm, viewSort } = useDataTableStore()
  const conditionGroup = useReportConditionStore((state) => state.conditionGroup)
  const conditionsInitialized = useReportConditionStore((state) => state.initialized)
  const normalizedGroup = useMemo(() => normalizeReportConditionGroup(conditionGroup), [conditionGroup])

  return useInfiniteQuery<
    PastClientsResponse,
    Error,
    { past_clients: PastClient[]; pagination: PaginationMetadata },
    unknown[],
    NextPageParams | undefined
  >({
    queryKey: ["past_clients", "report", "infinite", searchTerm, normalizedGroup, viewSort],
    staleTime: 0,
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchPastClients(context, normalizedGroup, pageParam, searchTerm, viewSort)
    },
    initialPageParam: undefined,
    enabled: enabled && conditionsInitialized,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: (data) => ({
      past_clients: data.pages.flatMap((page) => page.past_clients),
      pagination: data.pages[data.pages.length - 1]?.pagination,
    }),
  })
}

export function useInfiniteReportPeople(recordType: HouseholdRecordType, enabled?: boolean) {
  const getContext = useApiContext()
  const { searchTerm, viewSort } = useDataTableStore()
  const conditionGroup = useReportConditionStore((state) => state.conditionGroup)
  const conditionsInitialized = useReportConditionStore((state) => state.initialized)
  const normalizedGroup = useMemo(() => normalizeReportConditionGroup(conditionGroup), [conditionGroup])

  return useInfiniteQuery<
    HouseholdPeopleResponse,
    Error,
    { people: HouseholdPerson[]; pagination: PaginationMetadata },
    unknown[],
    NextPageParams | undefined
  >({
    queryKey: [`${recordType}s`, "people", "report", "infinite", searchTerm, normalizedGroup, viewSort],
    staleTime: 0,
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchHouseholdPeople(context, recordType, normalizedGroup, pageParam, searchTerm, viewSort)
    },
    initialPageParam: undefined,
    enabled: enabled && conditionsInitialized,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: (data) => ({
      people: data.pages.flatMap((page) => page.people),
      pagination: data.pages[data.pages.length - 1]?.pagination,
    }),
  })
}

export function useInfiniteReportClientPeople(enabled?: boolean) {
  return useInfiniteReportPeople("client", enabled)
}

export function useInfiniteReportProspectPeople(enabled?: boolean) {
  return useInfiniteReportPeople("prospect", enabled)
}

export function useInfiniteReportContacts(enabled?: boolean) {
  const getContext = useApiContext()
  const { searchTerm, viewSort } = useDataTableStore()
  const conditionGroup = useReportConditionStore((state) => state.conditionGroup)
  const conditionsInitialized = useReportConditionStore((state) => state.initialized)
  const normalizedGroup = useMemo(() => normalizeReportConditionGroup(conditionGroup), [conditionGroup])

  return useInfiniteQuery<
    ContactListResponse,
    Error,
    { contacts: Contact[]; pagination: PaginationMetadata },
    unknown[],
    NextPageParams | undefined
  >({
    queryKey: ["contacts", "report", "infinite", searchTerm, normalizedGroup, viewSort],
    staleTime: 0,
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchContacts(context, normalizedGroup, pageParam, searchTerm, viewSort)
    },
    initialPageParam: undefined,
    enabled: enabled && conditionsInitialized,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: (data) => ({
      contacts: data.pages.flatMap((page) => page.contacts),
      pagination: data.pages[data.pages.length - 1]?.pagination,
    }),
  })
}

export function useInfiniteReportBusinesses(enabled?: boolean) {
  const getContext = useApiContext()
  const { searchTerm, viewSort } = useDataTableStore()
  const conditionGroup = useReportConditionStore((state) => state.conditionGroup)
  const conditionsInitialized = useReportConditionStore((state) => state.initialized)
  const normalizedGroup = useMemo(() => normalizeReportConditionGroup(conditionGroup), [conditionGroup])

  return useInfiniteQuery<
    BusinessesResponse,
    Error,
    { businesses: Business[]; pagination: PaginationMetadata },
    unknown[],
    NextPageParams | undefined
  >({
    queryKey: ["businesses", "report", "infinite", searchTerm, normalizedGroup, viewSort],
    staleTime: 0,
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchBusinesses(context, normalizedGroup, pageParam, searchTerm, viewSort)
    },
    initialPageParam: undefined,
    enabled: enabled && conditionsInitialized,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params ?? undefined,
    select: (data) => ({
      businesses: data.pages.flatMap((page) => page.businesses),
      pagination: data.pages[data.pages.length - 1]?.pagination,
    }),
  })
}
